import { t } from '../i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { CalendarDays, ChevronDown, ChevronRight, Delete, X } from 'lucide-react';
import {
  CURRENCIES,
  RATE_ONE_PPM,
  convertMinor,
  crossRatePpm,
  formatPeriod,
  isCurrencyCode,
  periodOfDate,
  shiftIsoDate,
  todayInTz,
  type CurrencyCode,
  type TransactionDto,
} from '@trestly/shared';
import { useBudgets, useCategories, useCreateTransaction, useRate, useSettings } from '../api/hooks';
import { usePeriod } from '../period-context';
import { tryParseAmount } from './AmountInput';
import { useSheetDrag } from './useSheetDrag';
import { formatIsoDate } from '../lib/format';
import { errorText } from '../lib/errors';
import { lookOf, NO_CATEGORY, useEnvelopeLook } from '../lib/envelope';
import { currencySign, money, moneyLabel } from '../lib/money';
import { haptic, useClosingConfirmation } from '@/telegram';
import { cn } from '@/lib/utils';

const LAST_CURRENCY_KEY = 'trestly:last-currency';
const LAST_CATEGORY_KEY = 'trestly:last-category';

/** До 9 999 999 с копейками: длиннее сумма — почти наверняка опечатка. */
const MAX_WHOLE_DIGITS = 7;

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'] as const;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* хранилище недоступно — просто не запоминаем */
  }
}

/** Последняя валюта запоминается: типовой случай — в два касания. */
function readLastCurrency(): CurrencyCode {
  const stored = readStorage(LAST_CURRENCY_KEY);
  return isCurrencyCode(stored) ? stored : 'BYN';
}

/** Последний конверт: им открывается трата из «Ленты», где конверт не выбран тапом. */
export function readLastCategory(): number | null {
  const stored = readStorage(LAST_CATEGORY_KEY);
  if (stored === null || stored === '') return null;
  const id = Number(stored);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Нажатие клавиши на своей клавиатуре. Строка остаётся тем же вводом, что
 * разбирает parseAmountToMinor: цифры и одна точка, не больше минорных знаков.
 */
export function pressKey(value: string, key: string, currency: CurrencyCode): string {
  const minorUnits = CURRENCIES[currency].minorUnits;
  if (key === 'back') return value.slice(0, -1);
  if (key === '.') {
    if (minorUnits === 0 || value.includes('.')) return value;
    return `${value === '' ? '0' : value}.`;
  }
  if (!/^\d$/.test(key)) return value;

  const [whole = '', frac] = value.split('.');
  if (frac !== undefined) return frac.length >= minorUnits ? value : value + key;
  if (whole === '0') return key;
  if (whole.length >= MAX_WHOLE_DIGITS) return value;
  return value + key;
}

interface EntrySheetProps {
  open: boolean;
  /** Конверт, из которого открыт лист. null — «Без категории». */
  categoryId: number | null;
  /** Показать выбор конверта сразу: лист открыт кнопкой «+», а не из конверта. */
  pick?: boolean;
  onClose: () => void;
  onCreated: (tx: TransactionDto) => void;
}

/**
 * Ввод траты из конверта: конверт уже выбран тапом по плитке, лист в его цвете.
 * Сумма и «Записать» — два касания; дата, заметка и другой конверт — по желанию.
 * Под названием видно, сколько останется в конверте после траты, ещё до записи.
 */
export function EntrySheet({ open, categoryId: initialCategoryId, pick = false, onClose, onCreated }: EntrySheetProps): JSX.Element {
  const { cycleStartDay } = usePeriod();
  const categories = useCategories();
  const settings = useSettings();
  const create = useCreateTransaction();
  const lookFor = useEnvelopeLook();

  const today = todayInTz();
  const [categoryId, setCategoryId] = useState<number | null>(initialCategoryId);
  const [picking, setPicking] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>(readLastCurrency);
  const [spentOn, setSpentOn] = useState(today);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    // Запомненный конверт могли убрать в архив: трата в него пропала бы с главной.
    const active = categories.data;
    setCategoryId(
      initialCategoryId !== null && active && !active.some((category) => category.id === initialCategoryId)
        ? null
        : initialCategoryId,
    );
    setPicking(pick);
    setAmount('');
    setNote('');
    setSpentOn(todayInTz());
    setCurrency(readLastCurrency());
    create.reset();
    // мутация меняется каждый рендер — намеренно не в зависимостях
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCategoryId, pick]);

  const sheetRef = useRef<HTMLDivElement>(null);
  const drag = useSheetDrag(sheetRef, onClose);

  // Введена сумма — закрытие свайпом клиента спросит подтверждение.
  useClosingConfirmation(open && amount !== '');

  const look = lookFor(categoryId);
  // Остаток — в том периоде, куда ляжет трата по своей дате, а не в том, что открыт на экране.
  const budgets = useBudgets(formatPeriod(periodOfDate(spentOn, cycleStartDay)));
  const budget = budgets.data?.items.find((item) => item.categoryId === categoryId);
  const baseCurrency = settings.data?.baseCurrency ?? budgets.data?.baseCurrency ?? 'BYN';

  const normalized = amount.endsWith('.') ? amount.slice(0, -1) : amount;
  const amountMinor = tryParseAmount(normalized, currency);
  const rate = useRate(today);
  // Остаток после траты — в базовой валюте, по курсу дня; арифметика только из shared.
  const usdPpm = rate.data?.ratePpm;
  const baseMinor =
    amountMinor === undefined
      ? 0
      : currency === baseCurrency
        ? amountMinor
        : usdPpm === undefined
          ? 0
          : convertMinor(
              amountMinor,
              crossRatePpm(
                currency === 'USD' ? usdPpm : RATE_ONE_PPM,
                baseCurrency === 'USD' ? usdPpm : RATE_ONE_PPM,
              ),
            );
  const remaining = budget && budget.limitMinor > 0 ? budget.limitMinor - budget.spentMinor : null;
  const ready = amountMinor !== undefined && amountMinor > 0 && !create.isPending;

  const press = useCallback(
    (key: string) => {
      haptic.tap(key === 'back' ? 'rigid' : 'light');
      setAmount((value) => pressKey(value, key, currency));
    },
    [currency],
  );

  const submit = (): void => {
    if (!ready || amountMinor === undefined) return;
    create.mutate(
      {
        amountMinor,
        currency,
        categoryId,
        spentOn,
        note: note.trim() || null,
      },
      {
        onSuccess: (tx) => {
          haptic.result('success');
          writeStorage(LAST_CURRENCY_KEY, currency);
          writeStorage(LAST_CATEGORY_KEY, categoryId === null ? '' : String(categoryId));
          onCreated(tx);
          onClose();
        },
        onError: () => haptic.result('error'),
      },
    );
  };

  const yesterday = shiftIsoDate(today, -1);
  const isCustomDate = spentOn !== today && spentOn !== yesterday;
  const [whole = '0', frac] = (amount || '0').split('.');
  const envelopes = [...(categories.data ?? [])].sort((a, b) => a.position - b.position);

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 animate-fade-in bg-[var(--overlay)] data-[state=closed]:animate-fade-out" />
        <Dialog.Content
          ref={sheetRef}
          className="fixed inset-x-0 bottom-0 z-50 mx-auto max-h-[calc(100dvh-var(--safe-top)-8px)] max-w-lg animate-sheet-in overflow-y-auto data-[state=closed]:animate-sheet-out rounded-t-[30px] text-foreground outline-none"
          style={
            {
              '--c': look.color,
              background: `color-mix(in srgb, ${look.color} var(--sheet-tint), var(--paper))`,
              paddingBottom: 'calc(14px + var(--safe-bottom))',
            } as React.CSSProperties
          }
          // Своя клавиатура: фокус в «Заметку» поднял бы системную.
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {/* Зона захвата: полоска и шапка — свайп вниз закрывает лист. */}
          <div className="cursor-grab touch-none select-none" {...drag}>
            <div className="mx-auto mt-2 h-[5px] w-9 rounded-full bg-border-strong" />
            <div className="flex items-start justify-between gap-3 px-5 pt-3">
              <div className="min-w-0">
                <Dialog.Title asChild>
                  <button
                    type="button"
                    className="flex max-w-full items-center gap-2 text-[17px] font-semibold active:opacity-60"
                    aria-expanded={picking}
                    onClick={() => {
                      haptic.select();
                      setPicking((value) => !value);
                    }}
                  >
                    <span className="cat-dot !size-3" />
                    <span className="truncate">{look.name}</span>
                    <ChevronDown
                      className={cn('size-4 shrink-0 text-muted-foreground transition-transform', picking && 'rotate-180')}
                      aria-hidden
                    />
                  </button>
                </Dialog.Title>
                {remaining !== null ? (
                  <div className="num mt-0.5 pl-5 text-[13px] text-muted-foreground">
                    {t('components.entry.left', { sum: money(remaining, baseCurrency) })}
                    {baseMinor > 0 ? (
                      <>
                        {' → '}
                        <span
                          className={cn(
                            'font-semibold',
                            remaining - baseMinor < 0 ? 'text-destructive' : 'text-foreground',
                          )}
                        >
                          {money(remaining - baseMinor, baseCurrency)}
                        </span>
                      </>
                    ) : null}
                  </div>
                ) : categoryId !== null ? (
                  <div className="mt-0.5 pl-5 text-[13px] text-muted-foreground">{t('components.noLimit')}</div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Link
                  to={categoryId === null ? '/envelopes/none' : `/envelopes/${categoryId}`}
                  className="flex h-9 items-center gap-0.5 rounded-full bg-glass pr-2 pl-3 text-[13px] font-medium text-muted-foreground no-underline active:opacity-60"
                  onClick={onClose}
                >
                  {t('components.entry.envelope')}
                  <ChevronRight className="size-4" aria-hidden />
                </Link>
                <Dialog.Close
                  className="flex size-9 items-center justify-center rounded-full bg-glass text-muted-foreground active:opacity-60"
                  aria-label={t('components.close')}
                >
                  <X className="size-4" aria-hidden />
                </Dialog.Close>
              </div>
            </div>
          </div>
          <Dialog.Description className="sr-only">{t('components.entry.description', { name: look.name })}</Dialog.Description>

          {picking ? (
            <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto px-5" role="group" aria-label={t('components.entry.envelope')}>
              {envelopes.map((category) => {
                const item = lookOf(category);
                return (
                  <button
                    key={category.id}
                    type="button"
                    className="glass-chip"
                    style={{ '--c': item.color } as React.CSSProperties}
                    aria-pressed={categoryId === category.id}
                    onClick={() => {
                      haptic.select();
                      setCategoryId(category.id);
                      setPicking(false);
                    }}
                  >
                    <span className="cat-dot" style={{ background: categoryId === category.id ? '#fff' : item.color }} />
                    {item.name}
                  </button>
                );
              })}
              <button
                type="button"
                className="glass-chip"
                style={{ '--c': NO_CATEGORY.color } as React.CSSProperties}
                aria-pressed={categoryId === null}
                onClick={() => {
                  haptic.select();
                  setCategoryId(null);
                  setPicking(false);
                }}
              >
                {NO_CATEGORY.name}
              </button>
            </div>
          ) : null}

          {/* Сумма: копейки мельче — крупной остаётся та часть, что важна. */}
          <div className="flex items-baseline justify-center gap-2 px-5 pt-5 pb-4">
            <span className={cn('num text-[60px] leading-none font-bold', amount === '' && 'opacity-30')}>
              {whole}
              {frac !== undefined ? <span className="text-[38px]">.{frac}</span> : null}
            </span>
            <button
              type="button"
              className="glass-chip !h-9 !px-3.5 !text-[22px] !font-semibold"
              aria-label={t('components.entry.currencySwitch', { currency })}
              onClick={() => {
                haptic.select();
                setCurrency((value) => (value === 'BYN' ? 'USD' : 'BYN'));
              }}
            >
              {currencySign(currency)}
            </button>
          </div>

          <div className="no-scrollbar flex gap-2 overflow-x-auto px-5">
            <button type="button" className="glass-chip" aria-pressed={spentOn === today} onClick={() => setSpentOn(today)}>
              {t('components.today')}
            </button>
            <button
              type="button"
              className="glass-chip"
              aria-pressed={spentOn === yesterday}
              onClick={() => setSpentOn(yesterday)}
            >
              {t('components.yesterday')}
            </button>
            <label className="glass-chip relative" aria-pressed={isCustomDate}>
              <CalendarDays className="size-4" aria-hidden />
              {isCustomDate ? formatIsoDate(spentOn) : t('components.date')}
              <input
                type="date"
                max={today}
                value={spentOn}
                onChange={(event) => event.target.value && setSpentOn(event.target.value)}
                className="absolute inset-0 opacity-0"
                aria-label={t('components.txDate')}
              />
            </label>
          </div>

          <div className="px-5 pt-3">
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('components.note')}
              maxLength={200}
              className="h-11 w-full rounded-2xl bg-glass px-4 text-[15px] outline-none placeholder:text-faint"
            />
          </div>

          <div className="grid grid-cols-3 gap-2 px-5 pt-3">
            {KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className="numkey"
                onClick={() => press(key)}
                aria-label={key === 'back' ? t('components.entry.erase') : key}
              >
                {key === 'back' ? <Delete className="size-6" aria-hidden /> : key}
              </button>
            ))}
          </div>

          {create.isError ? (
            <div className="px-5 pt-3 text-[13px] text-destructive">{errorText(create.error)}</div>
          ) : null}

          <div className="px-5 pt-3">
            <button
              type="button"
              disabled={!ready}
              onClick={submit}
              className="h-14 w-full rounded-[18px] text-[17px] font-semibold text-white transition-opacity active:opacity-85 disabled:opacity-40"
              style={{ background: look.color }}
            >
              {amountMinor ? t('components.entry.submit', { sum: moneyLabel(amountMinor, currency) }) : t('components.amountRequired')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}